import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  recomendaciones,
  bloquesInfo,
  reglasDescubrimiento,
  statsDescubrimiento,
  tiposEntidad,
  getRecomendacionesPorBloque,
  getTodasPorBloque,
  formatearNumero,
  type BloqueDescubrimiento,
  type TipoEntidadDescubrimiento,
  type RecomendacionDescubrimiento,
} from '@/mocks/sistemaDescubrimiento';

function RecomendacionCard({ rec }: { rec: RecomendacionDescubrimiento }) {
  const tipoInfo = tiposEntidad.find(t => t.id === rec.tipo);
  const bloqueInfo = bloquesInfo.find(b => b.id === rec.bloque);

  return (
    <div className="group bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden hover:border-background-300/60 transition-all cursor-pointer">
      <div className="relative h-40 overflow-hidden">
        <img src={rec.imagenUrl} alt={rec.nombre} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent"></div>
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${tipoInfo?.color} ${tipoInfo?.bg} bg-white/90`}>
            <i className={`${tipoInfo?.icono} text-[9px]`}></i>
            {tipoInfo?.label}
          </span>
          {rec.estaEnVivo && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              EN VIVO
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3">
          {bloqueInfo && (
            <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-bold ${bloqueInfo.bg} ${bloqueInfo.color}`}>
              <i className={`${bloqueInfo.icono} text-[8px]`}></i>
            </span>
          )}
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-sm font-bold text-white truncate drop-shadow-md">{rec.nombre}</h3>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <img src={rec.logoUrl} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-xs text-foreground-700 truncate">{rec.subtitulo}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] text-foreground-500">{rec.ciudad}</span>
              <span className="text-[10px] text-foreground-400">{rec.categoria}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 text-[10px] text-foreground-500">
          <span className="flex items-center gap-1" title="Likes">
            <i className="ri-heart-line text-[9px] text-rose-500"></i>
            {rec.senales.likes}
          </span>
          <span className="flex items-center gap-1" title="Comentarios">
            <i className="ri-chat-1-line text-[9px] text-amber-500"></i>
            {rec.senales.comentarios}
          </span>
          <span className="flex items-center gap-1" title="Compartidos">
            <i className="ri-share-forward-line text-[9px] text-emerald-500"></i>
            {rec.senales.compartidos}
          </span>
          <span className="flex items-center gap-1 ml-auto" title="Interaction Score">
            <span className={`text-[9px] font-bold ${rec.interactionScore >= 90 ? 'text-rose-600' : rec.interactionScore >= 80 ? 'text-amber-600' : 'text-emerald-600'}`}>
              {rec.interactionScore} pts
            </span>
          </span>
        </div>

        {rec.badge && (
          <span className="inline-block px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[9px] font-semibold">{rec.badge}</span>
        )}
      </div>
    </div>
  );
}

function FeaturedRecomendacionCard({ rec }: { rec: RecomendacionDescubrimiento }) {
  const tipoInfo = tiposEntidad.find(t => t.id === rec.tipo);
  const bloqueInfo = bloquesInfo.find(b => b.id === rec.bloque);

  return (
    <div className="group relative h-[260px] rounded-2xl overflow-hidden bg-background-100 cursor-pointer">
      <img src={rec.imagenUrl} alt={rec.nombre} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent"></div>

      <div className="absolute top-4 left-4 flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border ${tipoInfo?.color} ${tipoInfo?.bg} bg-white/90`}>
          <i className={`${tipoInfo?.icono} text-[9px]`}></i>
          {tipoInfo?.label}
        </span>
        {rec.estaEnVivo && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500 text-white text-[9px] font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
            EN VIVO
          </span>
        )}
        {bloqueInfo && (
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold ${bloqueInfo.bg} ${bloqueInfo.color}`}>
            <i className={`${bloqueInfo.icono} text-[9px]`}></i>
            {bloqueInfo.titulo}
          </span>
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-6">
        <div className="flex items-center gap-3 mb-3">
          <img src={rec.logoUrl} alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-white/20" />
          <div>
            <h3 className="text-lg font-bold text-white drop-shadow-md">{rec.nombre}</h3>
            <p className="text-sm text-white/70 mt-0.5">{rec.subtitulo}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs text-white/60">
          <span className="flex items-center gap-1 bg-white/10 px-2 py-0.5 rounded-full">
            <i className="ri-heart-line text-[10px] text-rose-400"></i>
            {rec.senales.likes}
          </span>
          <span className="flex items-center gap-1 bg-white/10 px-2 py-0.5 rounded-full">
            <i className="ri-chat-1-line text-[10px] text-amber-400"></i>
            {rec.senales.comentarios}
          </span>
          <span className="flex items-center gap-1 bg-white/10 px-2 py-0.5 rounded-full">
            <i className="ri-share-forward-line text-[10px] text-emerald-400"></i>
            {rec.senales.compartidos}
          </span>
          <span className={`text-[10px] font-bold ${rec.interactionScore >= 90 ? 'text-rose-400' : 'text-amber-400'}`}>
            {rec.interactionScore} pts interacción
          </span>
        </div>

        {rec.badge && (
          <span className="inline-block mt-2 px-2.5 py-0.5 rounded-full bg-rose-500/80 text-white text-[10px] font-semibold">{rec.badge}</span>
        )}
        <p className="text-xs text-white/50 mt-2 italic">"{rec.motivo}"</p>
      </div>
    </div>
  );
}

function ReglaCard({ regla }: { regla: typeof reglasDescubrimiento[0] }) {
  return (
    <div className={`bg-background-50 rounded-2xl border ${regla.colorBorde} p-5 space-y-3`}>
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${regla.gradiente} flex items-center justify-center`}>
        <i className={`${regla.icono} text-white text-lg`}></i>
      </div>
      <h4 className="text-sm font-bold text-foreground-900">{regla.titulo}</h4>
      <p className="text-xs text-foreground-600 leading-relaxed">{regla.descripcion}</p>
    </div>
  );
}

export default function DescubrimientoSection() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<BloqueDescubrimiento | 'reglas'>('porque-te-gusto');

  const porBloque = getTodasPorBloque();
  const currentBloque = activeTab !== 'reglas' ? bloquesInfo.find(b => b.id === activeTab) : null;
  const currentItems = activeTab !== 'reglas' ? getRecomendacionesPorBloque(activeTab) : [];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-xs font-bold mb-4">
            <i className="ri-compass-3-line"></i>
            DESCUBRIMIENTO PERSONALIZADO
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3">Encontrá Tu Próximo Contenido Favorito</h2>
          <p className="text-base text-foreground-600 max-w-2xl mx-auto">Recomendaciones inteligentes basadas en tus interacciones, no solo en popularidad. Cuanto más usás RFW, más preciso se vuelve.</p>

          {/* Mini stats */}
          <div className="flex flex-wrap justify-center gap-6 mt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground-950">{statsDescubrimiento.totalRecomendaciones}</p>
              <p className="text-xs text-foreground-500">Recomendaciones</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-rose-600">{statsDescubrimiento.bloquesActivos}</p>
              <p className="text-xs text-foreground-500">Bloques</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground-950">{statsDescubrimiento.entidadesCubiertas}</p>
              <p className="text-xs text-foreground-500">Entidades</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">{statsDescubrimiento.precisionAlgoritmo}</p>
              <p className="text-xs text-foreground-500">Precisión</p>
            </div>
          </div>
        </div>

        {/* Pills de bloques */}
        <div className="flex flex-wrap justify-center gap-2 mb-2">
          {bloquesInfo.map(bloque => (
            <button
              key={bloque.id}
              onClick={() => setActiveTab(bloque.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                activeTab === bloque.id
                  ? 'bg-rose-500 text-white'
                  : 'bg-background-200/70 text-foreground-600 hover:bg-background-300/60'
              }`}
            >
              <i className={`${bloque.icono} text-xs`}></i>
              {bloque.titulo}
            </button>
          ))}
          <button
            onClick={() => setActiveTab('reglas')}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
              activeTab === 'reglas'
                ? 'bg-violet-500 text-white'
                : 'bg-background-200/70 text-foreground-600 hover:bg-background-300/60'
            }`}
          >
            <i className="ri-scales-3-line text-xs"></i>
            Reglas
          </button>
        </div>

        {/* Descripción del bloque activo */}
        {currentBloque && (
          <div className="text-center mb-8 mt-4">
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">{currentBloque.descripcion}</p>
          </div>
        )}

        {/* Contenido del bloque */}
        {activeTab !== 'reglas' && (
          <>
            {/* Featured (top 2) */}
            {currentItems.length >= 2 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
                <FeaturedRecomendacionCard rec={currentItems[0]} />
                <FeaturedRecomendacionCard rec={currentItems[1]} />
              </div>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {currentItems.map(rec => (
                <RecomendacionCard key={rec.id} rec={rec} />
              ))}
            </div>
          </>
        )}

        {/* Vista Reglas */}
        {activeTab === 'reglas' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {reglasDescubrimiento.map(r => (
                <ReglaCard key={r.id} regla={r} />
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 text-center">
          <button
            onClick={() => navigate('/sistema-descubrimiento')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 text-white font-bold text-sm hover:from-rose-600 hover:to-amber-600 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-compass-3-line"></i>
            Abrir Sistema de Descubrimiento Completo
          </button>
        </div>

        {/* Regla final */}
        <div className="mt-8 text-center">
          <p className="text-xs text-foreground-400 italic">"Priorizar interacción sobre popularidad. Una comunidad activa vale más que mil oyentes pasivos."</p>
        </div>
      </div>
    </section>
  );
}